/**
 * LITTER WIZARD - Kreator miotu
 * Entity Type: OFFER
 * 
 * Używa CORE validation + quality score z creators/
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import StudioShell from '@/components/studio/StudioShell';
import WizardSidebar from '@/pages/KennelWizardV2/WizardSidebar';
import LitterLivePreview from '@/components/wizard/LitterLivePreview';
import WizardSummaryStep from '@/components/wizard/WizardSummaryStep';
import PublishSuccessFlow from '@/components/wizard/PublishSuccessFlow';
import { WizardTopbar } from '@/components/wizard/WizardTopbar';
import { WizardMobileBar } from '@/components/wizard/WizardMobileBar';
import { WizardStep, WizardActions } from '@/components/WizardShell';
import { ImageSeoBadge } from '@/components/ImageSeoBadge';
import FontStylePicker from '@/components/wizard/FontStylePicker';
import LivePagePreview from '@/components/wizard/LivePagePreview';
import { ModerationInfoBox } from '@/components/ModerationStatus';
import ImageUploader from '@/components/ImageUploader';
import { fireConfetti } from '@/utils/confetti';
import { 
  ArrowLeft, ArrowRight, Loader2, FileText, Wand2, Calendar, 
  Image as ImageIcon, Dog, Heart, Users, Home, Building2,
  AlertCircle, TrendingUp, CheckCircle2, Phone, DollarSign, Camera,
  Plus, X, Shield, ChevronsUpDown, Check, Search, Inbox, FolderOpen
} from 'lucide-react';

import { API } from '@/utils/api';

// ==================== CORE CREATOR IMPORTS ====================
import { computeQualityScore, getStatusConfig } from '@/creators/core/qualityScore';
import { 
  litterOfferSchema, 
  litterSteps,
  PRICE_INFO_OPTIONS,
  HEALTH_TEST_OPTIONS,
  LITTER_STATUS_OPTIONS,
  getLitterMissingItems,
  hasSufficientPriceInfo,
  hasSufficientGallery,
  hasParentsInfo,
  hasMotherInfo,
  hasFatherInfo,
  IMAGE_REQUIREMENTS
} from '@/creators/schemas/litter.offer.schema';

import { DOG_BREEDS, CAT_BREEDS } from '@/data/breeds';

// ==================== QUALITY SCORE BAR ====================
function QualityScoreBar({ formData, onMissingClick }) {
  const scoreResult = computeQualityScore({ 
    entityType: 'OFFER', 
    formData, 
    schema: litterOfferSchema 
  });
  const { score, status, suggestions, missingMust } = scoreResult;
  const config = getStatusConfig(status);
  
  const StatusIcons = {
    red: AlertCircle,
    yellow: TrendingUp,
    green: CheckCircle2
  };
  const Icon = StatusIcons[status];
  
  // Get custom missing items for litter
  const customMissing = getLitterMissingItems(formData);
  const displayItems = customMissing.length > 0 ? customMissing : [
    ...missingMust.slice(0, 2),
    ...suggestions.slice(0, 2)
  ];

  return (
    <div className={`rounded-xl border ${config.border} ${config.bgLight} p-4 mb-6`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${config.color}`} />
          <span className={`font-medium ${config.color}`}>
            Jakość oferty: {score}%
          </span>
          <span className={`text-xs ${config.color} opacity-70`}>
            ({config.label})
          </span>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-zinc-200 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
        <motion.div 
          className={`h-full ${config.bg} rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      
      {/* Missing items - specific for OFFER */}
      {displayItems.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs text-zinc-500">Dodaj:</span>
          <div className="flex flex-wrap gap-2">
            {displayItems.slice(0, 4).map((item) => (
              <button
                key={item.field}
                onClick={() => onMissingClick?.(item)}
                className="text-xs px-2 py-1 bg-white dark:bg-gray-800 border border-zinc-200 dark:border-gray-600 rounded-full hover:border-[#F47A3B] hover:text-[#F47A3B] transition-colors"
              >
                + {item.message || item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== BREED COMBOBOX ====================
function LitterBreedCombobox({ breeds, value, onChange, hasError }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = search
    ? breeds.filter(b => b.toLowerCase().includes(search.toLowerCase()))
    : breeds;

  const handleSelect = (breed) => {
    onChange(breed);
    setOpen(false);
    setSearch('');
  };

  // Zatwierdź wpisany tekst (Enter lub zamknięcie listy) — bez konieczności klikania pozycji.
  const commitSearch = () => {
    const s = search.trim();
    if (!s) return;
    const exact = breeds.find(b => b.toLowerCase() === s.toLowerCase());
    if (exact) onChange(exact);
    else if (filtered.length > 0) onChange(filtered[0]);
    else if (s.length >= 2) onChange(s);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) commitSearch(); }}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open}
          className={`flex h-12 w-full items-center justify-between rounded-md border px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring ${
            hasError ? 'border-red-500' : 'border-input'
          } ${value ? 'text-foreground' : 'text-muted-foreground'}`}
          data-testid="litter-breed-select">
          {value || 'Wyszukaj lub wpisz rase...'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Wpisz nazwe rasy..." value={search} onValueChange={setSearch}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (filtered.length > 0) handleSelect(filtered[0]);
                else if (search.trim().length >= 2) { onChange(search.trim()); setOpen(false); setSearch(''); }
              }
            }}
            data-testid="litter-breed-search" />
          <CommandList className="max-h-60">
            <CommandEmpty className="py-3 px-4">
              {search.trim().length >= 2 ? (
                <button type="button" onClick={() => { onChange(search.trim()); setOpen(false); setSearch(''); }}
                  className="w-full text-left text-sm py-2 px-3 rounded-lg bg-orange-50 hover:bg-orange-100" data-testid="litter-breed-custom">
                  <span className="font-semibold text-[#F47A3B]">+ Dodaj:</span> <span className="font-medium">{search.trim()}</span>
                </button>
              ) : (
                <p className="text-sm text-muted-foreground">Wpisz min. 2 znaki...</p>
              )}
            </CommandEmpty>
            <CommandGroup>
              {filtered.map(breed => (
                <CommandItem key={breed} value={breed} onSelect={() => handleSelect(breed)} className="cursor-pointer">
                  <Check className={`mr-2 h-4 w-4 ${value === breed ? 'opacity-100' : 'opacity-0'}`} />
                  {breed}
                </CommandItem>
              ))}
            </CommandGroup>
            {search.trim().length >= 2 && filtered.length > 0 && !filtered.includes(search.trim()) && (
              <CommandGroup heading="Nie znalazles rasy?">
                <CommandItem onSelect={() => { onChange(search.trim()); setOpen(false); setSearch(''); }} className="cursor-pointer">
                  <Plus className="mr-2 h-4 w-4 text-[#F47A3B]" />
                  <span>Dodaj: <strong>{search.trim()}</strong></span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


// ==================== MAIN COMPONENT ====================
export default function LitterWizard() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Roboczy szkic miotu — działa jak w Kreatorze Hodowli.
  const draftRestoredRef = useRef(false);
  const draftKennelIdRef = useRef(null);
  const firstAutosaveRef = useRef(true);
  const LITTER_DRAFT_KEY = 'apisai_litter_wizard_draft';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingKennels, setLoadingKennels] = useState(true);
  const [loadingParents, setLoadingParents] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [kennels, setKennels] = useState([]);
  const [selectedKennel, setSelectedKennel] = useState(null);
  const [errors, setErrors] = useState({});
  const [publishedHandoff, setPublishedHandoff] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState(null);
  
  // Parents state for Step 2 (ID-based selection)
  const [availableMothers, setAvailableMothers] = useState([]);
  const [availableFathers, setAvailableFathers] = useState([]);
  const [useManualMother, setUseManualMother] = useState(false);
  const [useManualFather, setUseManualFather] = useState(false);
  
  // Quick Add Parent Modal state
  const [quickAddModal, setQuickAddModal] = useState({ open: false, type: null }); // type: 'mother' | 'father'
  const [quickAddForm, setQuickAddForm] = useState({ name: '', breed: '', gender: '' });
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  
  // Krok końcowy „Podsumowanie" — taki sam jak w Kreatorze Hodowli (spójność 1:1).
  const allLitterSteps = [
    ...litterSteps,
    { id: 7, name: 'Podsumowanie', subtitle: 'Opublikuj i zobacz efekty', icon: 'rocket', fields: [], mustFields: [], mustGate: false },
  ];
  const totalSteps = allLitterSteps.length;
  
  const [form, setForm] = useState({
    kennel_id: '',
    name: '',
    breed: '',
    species: 'dog',
    birth_date: '',
    planned_date: '',
    // Parent IDs (primary - linked to database)
    mother_id: '',
    father_stud_id: '',
    // Fallback text names (when parent not in system)
    mother_name: '',
    mother_titles: '',
    father_name: '',
    father_titles: '',
    sire_external: false,
    // Organization
    organization_type: '',
    organization_custom: '',
    is_fci: false,
    puppy_count_male: 0,
    puppy_count_female: 0,
    available_count: 0,
    reserved_count: 0,
    price: '',
    price_info: '',
    selected_price_info: [],
    pickup_date: '',
    description: '',
    health_tests: '',
    selected_health_tests: [],
    cover_image: '',
    gallery: [],
    status: 'available',
    show_in_catalog: true,
    font_style: 'elegant',
  });

  // Terms acceptance state
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Step names for display
  const stepNames = allLitterSteps.reduce((acc, s) => {
    acc[s.id] = s.name;
    return acc;
  }, {});

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      fetchKennels();
      if (isEdit) {
        fetchLitter();
      }
    }
  }, [user, authLoading, isEdit, id, navigate]);

  // Wczytaj roboczy szkic miotu (tylko nowy) — analogicznie do Kreatora Hodowli.
  useEffect(() => {
    if (isEdit) return;
    try {
      const raw = localStorage.getItem(LITTER_DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft?.form) {
          draftRestoredRef.current = true;
          draftKennelIdRef.current = draft.form.kennel_id || null;
          setForm((prev) => ({ ...prev, ...draft.form }));
          if (draft.step) setStep(draft.step);
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autozapis roboczego szkicu (tylko nowy). Pomijamy pierwszy przebieg (mount),
  // by nie nadpisać wczytanego szkicu pustym formularzem (race z efektem wczytania).
  useEffect(() => {
    if (isEdit) return;
    if (firstAutosaveRef.current) { firstAutosaveRef.current = false; return; }
    try {
      localStorage.setItem(LITTER_DRAFT_KEY, JSON.stringify({ form, step }));
    } catch {}
  }, [form, step, isEdit]);

  const fetchKennels = async () => {
    setLoadingKennels(true);
    try {
      const res = await axios.get(`${API}/kennels`);
      setKennels(res.data);
      if (res.data.length > 0 && !isEdit) {
        if (draftRestoredRef.current) {
          // Szkic wczytany — dopasuj wybraną hodowlę do draftu (lub pierwsza) i pobierz rodziców.
          const draftKennel = res.data.find(k => k.id === draftKennelIdRef.current) || res.data[0];
          setSelectedKennel(draftKennel);
          fetchParents(draftKennel.id);
        } else {
          const firstKennel = res.data[0];
          setForm(prev => ({ ...prev, kennel_id: firstKennel.id }));
          setSelectedKennel(firstKennel);
          // Also set breed and fetch parents
          if (firstKennel.breeds?.length > 0) {
            setForm(prev => ({ ...prev, breed: firstKennel.breeds[0] }));
          }
          // Fetch available parents for this kennel
          fetchParents(firstKennel.id);
        }
      }
    } catch (e) {
      toast.error('Błąd ładowania hodowli');
    } finally {
      setLoadingKennels(false);
    }
  };

  const fetchLitter = async () => {
    try {
      const res = await axios.get(`${API}/litters/${id}`);
      setForm({
        ...res.data,
        price: res.data.price?.toString() || '',
        show_in_catalog: res.data.show_in_catalog !== false,
      });
    } catch (e) {
      toast.error('Nie znaleziono miotu');
      navigate('/panel');
    }
  };

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear error for this field
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  // Fetch available parents when kennel changes
  const fetchParents = async (kennelId) => {
    if (!kennelId) return;
    setLoadingParents(true);
    try {
      const res = await axios.get(`${API}/litter-wizard/parents?kennel_id=${kennelId}`);
      setAvailableMothers(res.data.mothers || []);
      setAvailableFathers(res.data.fathers || []);
      
      // Auto-set breed from kennel if not already set
      if (res.data.kennel_breed && !form.breed) {
        updateForm('breed', res.data.kennel_breed);
      }
    } catch (e) {
      console.error('Error fetching parents:', e);
      // Not critical - user can still use manual entry
      setAvailableMothers([]);
      setAvailableFathers([]);
    } finally {
      setLoadingParents(false);
    }
  };

  const handleKennelChange = (kennelId) => {
    const kennel = kennels.find(k => k.id === kennelId);
    setSelectedKennel(kennel || null);
    updateForm('kennel_id', kennelId);
    if (kennel) {
      updateForm('species', kennel.species[0] || 'dog');
      if (kennel.breeds.length > 0) {
        updateForm('breed', kennel.breeds[0]);
      }
      // Inherit organization from kennel
      if (kennel.organization_type) {
        updateForm('organization_type', kennel.organization_type);
        updateForm('is_fci', kennel.is_fci || false);
      }
      // Fetch parents for this kennel
      fetchParents(kennelId);
    } else {
      // Bez hodowli — miot samodzielny: brak dziedziczonych rodziców (wpis ręczny).
      setAvailableMothers([]);
      setAvailableFathers([]);
    }
    // Reset parent selections when kennel changes
    setUseManualMother(false);
    setUseManualFather(false);
    updateForm('mother_id', '');
    updateForm('father_stud_id', '');
    updateForm('mother_name', '');
    updateForm('father_name', '');
  };
  
  // Handle mother selection from dropdown
  const handleMotherSelect = (motherId) => {
    if (motherId === 'manual') {
      setUseManualMother(true);
      updateForm('mother_id', '');
      return;
    }
    if (motherId === 'quick_add') {
      openQuickAddModal('mother');
      return;
    }
    setUseManualMother(false);
    updateForm('mother_id', motherId);
    
    // Auto-fill mother name from selected dog
    const mother = availableMothers.find(m => m.id === motherId);
    if (mother) {
      updateForm('mother_name', mother.name);
    }
  };
  
  // Handle father selection from dropdown
  const handleFatherSelect = (fatherId) => {
    if (fatherId === 'manual') {
      setUseManualFather(true);
      updateForm('father_stud_id', '');
      return;
    }
    if (fatherId === 'quick_add') {
      openQuickAddModal('father');
      return;
    }
    setUseManualFather(false);
    updateForm('father_stud_id', fatherId);
    
    // Auto-fill father name from selected stud
    const father = availableFathers.find(f => f.id === fatherId);
    if (father) {
      updateForm('father_name', father.name);
    }
  };
  
  // Quick Add Modal functions
  const openQuickAddModal = (type) => {
    const gender = type === 'mother' ? 'female' : 'male';
    setQuickAddForm({
      name: '',
      breed: form.breed || '',
      gender: gender
    });
    setQuickAddModal({ open: true, type });
  };
  
  const closeQuickAddModal = () => {
    setQuickAddModal({ open: false, type: null });
    setQuickAddForm({ name: '', breed: '', gender: '' });
  };
  
  const handleQuickAddSubmit = async () => {
    if (!quickAddForm.name || quickAddForm.name.trim().length < 2) {
      toast.error('Podaj imię (min. 2 znaki)');
      return;
    }
    if (!quickAddForm.breed) {
      toast.error('Wybierz rasę');
      return;
    }
    
    setQuickAddLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: quickAddForm.name.trim(),
        breed: quickAddForm.breed,
        gender: quickAddForm.gender,
        species: form.species,
        listing_type: 'kennel_dog'
      };
      
      // Use the quick-add endpoint (minimal fields required)
      const res = await axios.post(`${API}/kennels/${form.kennel_id}/dogs/quick-add`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newDog = res.data;
      toast.success(`${quickAddForm.gender === 'female' ? 'Suka' : 'Pies'} "${newDog.name}" dodany!`);
      
      // Refresh parents list
      await fetchParents(form.kennel_id);
      
      // Auto-select the new dog
      if (quickAddModal.type === 'mother') {
        setTimeout(() => {
          updateForm('mother_id', newDog.id);
          updateForm('mother_name', newDog.name);
          setUseManualMother(false);
        }, 100);
      } else {
        setTimeout(() => {
          updateForm('father_stud_id', newDog.id);
          updateForm('father_name', newDog.name);
          setUseManualFather(false);
        }, 100);
      }
      
      closeQuickAddModal();
    } catch (e) {
      console.error('Quick add error:', e);
      const detail = e.response?.data?.detail;
      let errorMsg = 'Błąd dodawania';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail.map(err => err.msg || 'Błąd walidacji').join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setQuickAddLoading(false);
    }
  };

  // ==================== ASYSTENT OPISU (bez nazwy „AI") ====================
  const generateDescription = async () => {
    if (!form.breed || (!form.mother_name && !form.mother_id)) {
      toast.error('Najpierw wypełnij rasę i dane rodziców');
      return;
    }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ai/generate`, {
        type: 'litter_description',
        litter_id: isEdit ? id : null,
        style: 'premium',
      }, { headers: { Authorization: `Bearer ${token}` } });
      updateForm('description', res.data.content);
      toast.success('Gotowe — propozycja opisu wstawiona');
    } catch (e) {
      toast.error('Nie udało się napisać opisu');
    } finally {
      setGenerating(false);
    }
  };

  // ==================== VALIDATION (SLIM FLOW) ====================
  // Tylko absolutne minimum gate'uje przejście dalej.
  // Step 1: kennel_id + name + breed + birth_date (MUST).
  // Step 2: rodzice — przynajmniej imię matki/ojca lub ID (MUST).
  // Step 3-6: zawsze valid (można pominąć z toastem).
  const validateStep = () => {
    if (step === 1) {
      const stepErrors = {};
      if (!form.name || form.name.trim().length < 1) {
        stepErrors.name = 'Podaj nazwę / literę miotu';
      }
      if (!form.breed) {
        stepErrors.breed = 'Wybierz rasę';
      }
      if (!form.birth_date) {
        stepErrors.birth_date = 'Podaj datę urodzenia';
      }
      setErrors(stepErrors);
      return Object.keys(stepErrors).length === 0;
    }

    if (step === 2) {
      const stepErrors = {};
      if (!hasMotherInfo(form)) {
        stepErrors.mother_id = 'Wybierz matkę z listy lub wpisz imię ręcznie';
      }
      if (!hasFatherInfo(form)) {
        stepErrors.father_stud_id = 'Wybierz ojca z listy lub wpisz imię ręcznie';
      }
      setErrors(stepErrors);
      return Object.keys(stepErrors).length === 0;
    }

    // Step 3-6 — opcjonalne, zawsze przepuszczamy.
    setErrors({});
    return true;
  };

  const isStepValid = () => {
    if (step === 1) {
      return !!form.name && form.name.trim().length >= 1 && !!form.breed && !!form.birth_date;
    }
    if (step === 2) {
      return hasMotherInfo(form) && hasFatherInfo(form);
    }
    // Pozostałe kroki — opcjonalne, zawsze można iść dalej.
    return true;
  };

  // Real-time error wyświetlanie tylko gdy user zacznie wypełniać (po opuszczeniu danego pola).
  // SLIM FLOW: nie spamujemy errorami zanim user zacznie pisać.
  useEffect(() => {
    if (step === 2 && (form.mother_id || form.mother_name || form.father_stud_id || form.father_name)) {
      const stepErrors = {};
      if (!hasMotherInfo(form)) {
        stepErrors.mother_id = 'Wybierz matkę z listy lub wpisz imię ręcznie';
      }
      if (!hasFatherInfo(form)) {
        stepErrors.father_stud_id = 'Wybierz ojca z listy lub wpisz imię ręcznie';
      }
      setErrors(stepErrors);
    }
  }, [form.mother_id, form.mother_name, form.father_stud_id, form.father_name, step]);

  const handleMissingFieldClick = (item) => {
    if (item.step && item.step !== step) {
      setStep(item.step);
      setTimeout(() => {
        const fieldEl = document.querySelector(`[data-testid="litter-${item.field}"], [name="${item.field}"]`);
        fieldEl?.focus?.();
        fieldEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const handleNext = () => {
    const isValid = validateStep();
    if (isValid) {
      setStep(step + 1);
      setErrors({});
      return;
    }
    // Pokaż konkretny komunikat, czego brakuje.
    if (step === 1) {
      if (!form.name || form.name.trim().length < 1) toast.error('Podaj nazwę / literę miotu');
      else if (!form.breed) toast.error('Wybierz rasę — wpisz i naciśnij Enter lub wybierz z listy');
      else if (!form.birth_date) toast.error('Podaj datę urodzenia');
    } else if (step === 2) {
      toast.error('Podaj rodziców miotu — wybierz z listy lub wpisz imiona');
    }
    // Scroll to first error
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const fieldEl = document.querySelector(`[data-testid="litter-${firstErrorField}"], [name="${firstErrorField}"]`);
      fieldEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setErrors({});
    }
  };

  const goToStep = (targetStep) => {
    setStep(targetStep);
    setErrors({});
  };

  // „Zapisz i wróć" — zapisujemy bieżący szkic lokalnie i wracamy do panelu (jak w Kreatorze Hodowli).
  const handleSaveAndExit = () => {
    if (!isEdit) {
      try { localStorage.setItem(LITTER_DRAFT_KEY, JSON.stringify({ form, step })); } catch {}
    }
    toast.success('Zapisano. Możesz wrócić w każdej chwili.');
    navigate('/panel');
  };

  // ==================== SUBMIT (SLIM FLOW) ====================
  // Blokujemy tylko na absolutnym minimum:
  // - nazwa, rasa, data urodzenia, hodowla
  // - rodzice (ID lub imię)
  // Reszta jest opcjonalna — można uzupełnić później z panelu.
  const handleSubmit = async () => {
    // Terms must be accepted on publish (new only)
    if (!isEdit && !termsAccepted) {
      toast.error('Zaakceptuj warunki publikacji');
      return;
    }

    // Minimum #1: podstawowe info (step 1). Hodowla jest OPCJONALNA.
    if (!form.name || form.name.trim().length < 1 || !form.breed || !form.birth_date) {
      toast.error('Uzupełnij podstawowe info: nazwa, rasa, data urodzenia');
      setStep(1);
      return;
    }

    // Minimum #2: rodzice (step 2).
    if (!hasParentsInfo(form)) {
      toast.error('Podaj rodziców miotu — wybierz z listy lub wpisz imiona');
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        price: form.price ? parseInt(form.price) : null,
        puppy_count_male: parseInt(form.puppy_count_male) || 0,
        puppy_count_female: parseInt(form.puppy_count_female) || 0,
        available_count: parseInt(form.available_count) || 0,
        reserved_count: parseInt(form.reserved_count) || 0,
        // Auto-set sire_external when using manual father name
        sire_external: !form.father_stud_id && !!form.father_name,
        // Send null instead of empty string for optional IDs
        mother_id: form.mother_id || null,
        father_stud_id: form.father_stud_id || null,
      };

      let res;
      if (isEdit) {
        res = await axios.put(`${API}/litters/${id}`, payload);
        toast.success('Miot zaktualizowany!');
        if (res.data?.slug) {
          navigate(`/miot/${res.data.slug}`);
        } else {
          navigate('/panel/moje');
        }
      } else {
        res = await axios.post(`${API}/litters`, payload);
        toast.success('Miot opublikowany!');
        fireConfetti();
        try { localStorage.removeItem(LITTER_DRAFT_KEY); } catch {}
        // AUTO-PROGRESSION: pokazujemy ujednolicony Ekran Sukcesu zamiast natychmiastowego redirectu.
        setPublishedSlug(res.data?.slug || null);
        setPublishedHandoff(true);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      let errorMsg = 'Błąd zapisu';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail.map(err => err.msg || 'Błąd walidacji').join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ==================== RENDER ====================
  if (authLoading || loadingKennels) {
    return (
      <StudioShell mode="workspace" contextLabel="Miot / szczeniaki" activeKey="litters" sidebarMuted={true}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--apisai-primary)]" />
        </div>
      </StudioShell>
    );
  }

  const currentBreeds = form.species === 'cat' ? CAT_BREEDS : DOG_BREEDS;

  // Tytuł lewej kolumny (KROKI) — kontekstowy do miotu.
  const sidebarTitle = (
    <>
      Budujemy prezentację{' '}
      <span className="text-orange-500">miotu</span>
    </>
  );

  // Pasek górny — wspólny WizardTopbar (jedno źródło prawdy dla wszystkich kreatorów)
  const litterProgressPct = Math.round((step / totalSteps) * 100);
  const litterCompletedSteps = Array.from({ length: Math.max(0, step - 1) }, (_, i) => i + 1);
  const litterTopbarSlot = (
    <WizardTopbar
      prefix="litter-wizard"
      step={step}
      totalSteps={totalSteps}
      stepName={stepNames[step]}
      progressPct={litterProgressPct}
      isLastStep={step === totalSteps}
      submitting={loading}
      nextDisabled={loading || (step === totalSteps && !termsAccepted && !isEdit)}
      publishLabel={isEdit ? 'Zapisz zmiany' : 'Opublikuj miot'}
      onSaveExit={handleSaveAndExit}
      onNext={step === totalSteps ? handleSubmit : handleNext}
      viewHref={form?.slug ? `/miot/${form.slug}` : undefined}
    />
  );

  // ==================== PODSUMOWANIE (krok końcowy) ====================
  const litterPuppyTotal = (parseInt(form.puppy_count_male) || 0) + (parseInt(form.puppy_count_female) || 0);
  const litterDescLen = (form.description || '').trim().length;
  const litterSummaryRows = [
    { icon: 'paw', label: 'Nazwa miotu', ok: !!form.name, value: form.name || 'Uzupełnij nazwę', hint: 'Krótka nazwa miotu (np. „Miot A 2026").', step: 1, required: true },
    { icon: 'dog', label: 'Rasa', ok: !!form.breed, value: form.breed || 'Wybierz rasę', hint: 'Rasa szczeniąt — po niej klienci filtrują katalog.', step: 1, required: true },
    { icon: 'star', label: 'Data urodzenia', ok: !!form.birth_date, value: form.birth_date || 'Podaj datę', hint: 'Data urodzenia pozwala policzyć wiek szczeniąt.', step: 1, required: true },
    { icon: 'parents', label: 'Rodzice', ok: !!(form.mother_name || form.father_name), value: [form.mother_name, form.father_name].filter(Boolean).join(' × ') || 'Brak rodziców', hint: 'Podaj sukę i reproduktora — rodowód buduje zaufanie.', step: 2, addLabel: 'Dodaj' },
    { icon: 'puppies', label: 'Szczenięta', ok: litterPuppyTotal > 0, value: litterPuppyTotal > 0 ? `${litterPuppyTotal} szczeniąt` : 'Brak liczby', hint: 'Ile szczeniąt jest dostępnych (samce/samice).', step: 3, addLabel: 'Dodaj' },
    { icon: 'money', label: 'Cena', ok: !!form.price, value: form.price ? `${form.price} zł` : 'Brak ceny', hint: 'Podaj cenę — to jedno z pierwszych pytań klientów.', step: 4, addLabel: 'Uzupełnij' },
    { icon: 'photo', label: 'Zdjęcie główne', ok: !!form.cover_image, value: form.cover_image ? 'Dodane' : 'Brak zdjęcia', hint: 'Zdjęcie szczeniąt przyciąga najwięcej uwagi.', step: 6, addLabel: 'Dodaj' },
    { icon: 'gallery', label: 'Galeria', ok: (form.gallery || []).length > 0, value: (form.gallery || []).length > 0 ? `${(form.gallery || []).length} zdjęć` : 'Brak zdjęć', hint: 'Dodaj zdjęcia poszczególnych szczeniąt.', step: 6, addLabel: 'Dodaj' },
    { icon: 'text', label: 'Opis miotu', ok: litterDescLen >= 80, value: litterDescLen ? (litterDescLen < 80 ? `Krótki opis (${litterDescLen} znaków)` : 'Dodany') : 'Brak opisu', hint: 'Opisz miot, socjalizację i warunki odbioru.', step: 5, addLabel: 'Uzupełnij' },
  ];

  const handleAddAnotherLitter = () => { setPublishedHandoff(false); window.location.assign('/wizard/miot'); };

  return (
    <StudioShell
      mode="workspace"
      contextLabel={isEdit ? 'Edytuj miot' : 'Dodaj miot / szczeniaki'}
      topbarSlot={litterTopbarSlot}
      activeKey="litters"
      sidebarMuted={true}
      backTo="/panel"
      backLabel="Wróć do panelu"
    >
      {publishedHandoff && (
        <PublishSuccessFlow
          entityType="miot"
          slug={publishedSlug}
          userName={user?.name}
          entityName={form.name}
          coverImage={form.cover_image || (form.gallery && form.gallery[0])}
          subtitle={form.breed}
          onClose={() => { setPublishedHandoff(false); navigate('/panel/mioty'); }}
          onNavigate={(path) => { setPublishedHandoff(false); navigate(path); }}
          nextItems={[
            { key: 'add', icon: Plus, label: 'Dodaj kolejny miot', action: handleAddAnotherLitter },
            { key: 'leads', icon: Inbox, label: 'Przejdź do zapytań', path: '/panel/zapytania' },
            { key: 'list', icon: FolderOpen, label: 'Lista miotów', path: '/panel/mioty' },
          ]}
        />
      )}
      <div data-testid="litter-wizard-v2">
      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 pb-24 lg:pb-8">
        <div className="flex flex-col lg:flex-row gap-6 xl:gap-8 items-start">
          {/* LEWA KOLUMNA: KROKI — ukryta na kroku Podsumowanie (pełna szerokość) */}
          {step !== totalSteps && (
          <WizardSidebar
            steps={allLitterSteps}
            currentStep={step}
            completedSteps={litterCompletedSteps}
            onStepClick={goToStep}
            title={sidebarTitle}
          />
          )}

          {/* ŚRODEK: FORMULARZ (na kroku Podsumowanie — treść od lewej, bez ramki karty; podgląd zostaje po prawej) */}
          <section className="flex-1 min-w-0 w-full" data-testid="litter-wizard-form-panel">
            <div className={step === totalSteps ? '' : 'apisai-soft-card overflow-hidden'}>
          <AnimatePresence mode="wait">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <WizardStep
                key="step1"
                icon={Dog}
                title="Podstawowe informacje"
                subtitle="Nazwa, rasa i data — od tego zaczyna się oferta widoczna w katalogu i Google."
              >
                <div className="space-y-6">
                  {/* Powiązanie z hodowlą — OPCJONALNE (miot może być samodzielną ofertą) */}
                  <div data-testid="litter-kennel-link">
                    <Label>Powiązanie z hodowlą (opcjonalnie)</Label>
                    {kennels.length > 0 ? (
                      <>
                        <Select
                          value={form.kennel_id || '__none__'}
                          onValueChange={(v) => handleKennelChange(v === '__none__' ? '' : v)}
                        >
                          <SelectTrigger data-testid="litter-kennel-select">
                            <SelectValue placeholder="Wybierz hodowlę lub zostaw bez powiązania" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Bez hodowli — samodzielna oferta</SelectItem>
                            {kennels.map((k) => (
                              <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-zinc-500 mt-1">
                          Możesz powiązać miot ze stroną hodowli teraz albo później. Bez powiązania miot działa jako samodzielna prezentacja.
                        </p>
                      </>
                    ) : (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="litter-no-kennel-hint">
                        Nie masz jeszcze hodowli — miot będzie samodzielną ofertą. Możesz powiązać go ze stroną hodowli później.
                      </div>
                    )}
                  </div>

                  {/* Inherited organization info */}
                  {form.organization_type && form.organization_type !== 'unknown' && form.organization_type !== '' && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="litter-org-badge">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-800 font-medium">
                        Organizacja: {form.organization_type === 'zkwp_fci' ? 'ZKwP (FCI)' : form.organization_type === 'fci_other' ? 'Inna FCI' : form.organization_type === 'khron' ? 'KHRON' : form.organization_type === 'wush' ? 'WUSH' : form.organization_type === 'other' ? 'Inna' : form.organization_type}
                        {form.is_fci && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-semibold">FCI</span>}
                      </span>
                    </div>
                  )}

                  {/* Litter Name */}
                  <div>
                    <Label>Nazwa/litera miotu *</Label>
                    <Input
                      data-testid="litter-name"
                      value={form.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      placeholder="np. Miot A"
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  {/* Species & Breed */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Gatunek</Label>
                      <Select value={form.species} onValueChange={(v) => updateForm('species', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dog">Pies</SelectItem>
                          <SelectItem value="cat">Kot</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Rasa *</Label>
                      <LitterBreedCombobox
                        breeds={currentBreeds}
                        value={form.breed}
                        onChange={(v) => updateForm('breed', v)}
                        hasError={!!errors.breed}
                      />
                      {errors.breed && <p className="text-red-500 text-sm mt-1">{errors.breed}</p>}
                    </div>
                  </div>

                  {/* Birth Date & Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Data urodzenia *</Label>
                      <Input
                        type="date"
                        data-testid="litter-birth_date"
                        value={form.birth_date}
                        onChange={(e) => updateForm('birth_date', e.target.value)}
                        className={errors.birth_date ? 'border-red-500' : ''}
                      />
                      {errors.birth_date && <p className="text-red-500 text-sm mt-1">{errors.birth_date}</p>}
                    </div>
                    <div>
                      <Label>Status miotu *</Label>
                      <Select value={form.status} onValueChange={(v) => updateForm('status', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LITTER_STATUS_OPTIONS.map(opt => (
                            <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* Step 2: Parents */}
            {step === 2 && (
              <WizardStep
                key="step2"
                icon={Heart}
                title="Rodzice miotu"
                subtitle="Rodzice (rodowód) pokazują wartość miotu i budują zaufanie kupujących."
              >
                <div className="space-y-6">
                  {/* Loading indicator */}
                  {loadingParents && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      <span className="text-sm text-blue-700">Ładowanie dostępnych rodziców...</span>
                    </div>
                  )}
                  
                  {/* Mother Section */}
                  <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-xl">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <span className="text-pink-500">♀</span> Matka (suka)
                    </h3>
                    
                    {/* Mother Selection - ID-based or Manual */}
                    {!useManualMother && availableMothers.length > 0 ? (
                      <div className="space-y-3">
                        <div>
                          <Label>Wybierz matkę z hodowli *</Label>
                          <Select 
                            value={form.mother_id} 
                            onValueChange={handleMotherSelect}
                          >
                            <SelectTrigger className={errors.mother_id ? 'border-red-500' : ''} data-testid="litter-mother_id">
                              <SelectValue placeholder="Wybierz sukę..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableMothers.map(mother => (
                                <SelectItem key={mother.id} value={mother.id}>
                                  <div className="flex items-center gap-2">
                                    {mother.main_photo && (
                                      <img src={mother.main_photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                                    )}
                                    <span>{mother.name}</span>
                                    {mother.breed && <span className="text-xs text-zinc-500">({mother.breed})</span>}
                                  </div>
                                </SelectItem>
                              ))}
                              <SelectItem value="quick_add">
                                <span className="text-[#F47A3B] font-medium flex items-center gap-1">
                                  <Plus className="w-4 h-4" /> Dodaj nową sukę
                                </span>
                              </SelectItem>
                              <SelectItem value="manual">
                                <span className="text-zinc-500">✎ Wpisz ręcznie (spoza bazy)</span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.mother_id && <p className="text-red-500 text-sm mt-1">{errors.mother_id}</p>}
                        </div>
                        
                        {/* Show selected mother info */}
                        {form.mother_id && (
                          <div className="text-xs text-zinc-500 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Matka powiązana z profilem w bazie - dane będą dziedziczone automatycznie
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Manual mother entry */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Imię matki *</Label>
                            <Input
                              data-testid="litter-mother_name"
                              value={form.mother_name}
                              onChange={(e) => updateForm('mother_name', e.target.value)}
                              placeholder="np. Luna"
                              className={errors.mother_name ? 'border-red-500' : ''}
                            />
                            {errors.mother_name && <p className="text-red-500 text-sm mt-1">{errors.mother_name}</p>}
                          </div>
                          <div>
                            <Label>Tytuły</Label>
                            <Input
                              value={form.mother_titles}
                              onChange={(e) => updateForm('mother_titles', e.target.value)}
                              placeholder="np. Ch., HD A"
                            />
                          </div>
                        </div>
                        
                        {/* Switch back to dropdown if dogs available */}
                        {availableMothers.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setUseManualMother(false)}
                            className="text-xs text-pink-600 hover:text-pink-700"
                          >
                            ← Wybierz z listy psów w hodowli
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Father Section */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <span className="text-blue-500">♂</span> Ojciec (reproduktor)
                    </h3>
                    
                    {/* Father Selection - ID-based or Manual */}
                    {!useManualFather && availableFathers.length > 0 ? (
                      <div className="space-y-3">
                        <div>
                          <Label>Wybierz ojca / reproduktora *</Label>
                          <Select 
                            value={form.father_stud_id} 
                            onValueChange={handleFatherSelect}
                          >
                            <SelectTrigger className={errors.father_stud_id ? 'border-red-500' : ''} data-testid="litter-father_stud_id">
                              <SelectValue placeholder="Wybierz reproduktora..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableFathers.map(father => (
                                <SelectItem key={father.id} value={father.id}>
                                  <div className="flex items-center gap-2">
                                    {father.main_photo && (
                                      <img src={father.main_photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                                    )}
                                    <span>{father.name}</span>
                                    {father.source === 'stud' && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded">Reproduktor</span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                              <SelectItem value="quick_add">
                                <span className="text-[#F47A3B] font-medium flex items-center gap-1">
                                  <Plus className="w-4 h-4" /> Dodaj nowego psa
                                </span>
                              </SelectItem>
                              <SelectItem value="manual">
                                <span className="text-zinc-500">✎ Wpisz ręcznie (spoza bazy)</span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.father_stud_id && <p className="text-red-500 text-sm mt-1">{errors.father_stud_id}</p>}
                        </div>
                        
                        {/* Show selected father info */}
                        {form.father_stud_id && (
                          <div className="text-xs text-zinc-500 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Ojciec powiązany z profilem reproduktora - klienci zobaczą link do jego strony
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Manual father entry */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Imię ojca *</Label>
                            <Input
                              data-testid="litter-father_name"
                              value={form.father_name}
                              onChange={(e) => updateForm('father_name', e.target.value)}
                              placeholder="np. Max"
                              className={errors.father_name ? 'border-red-500' : ''}
                            />
                            {errors.father_name && <p className="text-red-500 text-sm mt-1">{errors.father_name}</p>}
                          </div>
                          <div>
                            <Label>Tytuły</Label>
                            <Input
                              value={form.father_titles}
                              onChange={(e) => updateForm('father_titles', e.target.value)}
                              placeholder="np. Ch., HD A/A"
                            />
                          </div>
                        </div>
                        
                        {/* Switch back to dropdown if studs available */}
                        {availableFathers.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setUseManualFather(false)}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            ← Wybierz z listy reproduktorów
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Health Tests */}
                  <div>
                    <Label>Badania zdrowotne rodziców</Label>
                    <p className="text-xs text-zinc-500 mb-3">Zwiększa zaufanie kupujących</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {HEALTH_TEST_OPTIONS.map(test => (
                        <label key={test.id} className="flex items-center gap-2 p-2 rounded-lg border border-zinc-200 dark:border-gray-700 hover:bg-zinc-50 dark:hover:bg-gray-800 cursor-pointer">
                          <Checkbox
                            checked={form.selected_health_tests.includes(test.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                updateForm('selected_health_tests', [...form.selected_health_tests, test.id]);
                              } else {
                                updateForm('selected_health_tests', form.selected_health_tests.filter(t => t !== test.id));
                              }
                            }}
                          />
                          <span className="text-sm">{test.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* Step 3: Puppies Count */}
            {step === 3 && (
              <WizardStep
                key="step3"
                icon={Users}
                title="Szczenięta"
                subtitle="Liczba i płeć szczeniąt — kupujący od razu widzą dostępność."
              >
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Samce ♂</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.puppy_count_male}
                        onChange={(e) => updateForm('puppy_count_male', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Samice ♀</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.puppy_count_female}
                        onChange={(e) => updateForm('puppy_count_female', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Dostępnych</Label>
                      <Input
                        type="number"
                        min="0"
                        data-testid="litter-available_count"
                        value={form.available_count}
                        onChange={(e) => updateForm('available_count', e.target.value)}
                      />
                      <p className="text-xs text-zinc-500 mt-1">Ile można jeszcze zarezerwować</p>
                    </div>
                    <div>
                      <Label>Zarezerwowanych</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.reserved_count}
                        onChange={(e) => updateForm('reserved_count', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* Step 4: Price - CRITICAL FOR OFFER */}
            {step === 4 && (
              <WizardStep
                key="step4"
                icon={DollarSign}
                title="Cena i oferta"
                subtitle="Cena i warunki — jasna oferta przyspiesza decyzję kupującego."
              >
                <div className="space-y-6">
                  {/* Price */}
                  <div>
                    <Label className="flex items-center gap-2">
                      Cena szczenięcia (PLN) *
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">WYMAGANE</span>
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      data-testid="litter-price"
                      value={form.price}
                      onChange={(e) => updateForm('price', e.target.value ? Number(e.target.value) : '')}
                      placeholder="np. 5000"
                      className={errors.price ? 'border-red-500' : ''}
                    />
                    {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
                    <p className="text-xs text-zinc-500 mt-1">Cena jest wymagana dla oferty sprzedaży</p>
                  </div>

                  {/* Price Includes - MIN 4 REQUIRED */}
                  <div>
                    <Label className="flex items-center gap-2">
                      Co zawiera cena? *
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">MIN. 4</span>
                    </Label>
                    <p className="text-xs text-zinc-500 mb-3">
                      Wybierz min. 2 pozycje - klienci muszą wiedzieć, co dostaną
                      ({form.selected_price_info.length}/4 wybranych)
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {PRICE_INFO_OPTIONS.map(opt => (
                        <label 
                          key={opt.id} 
                          className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors
                            ${form.selected_price_info.includes(opt.id) 
                              ? 'border-[#F47A3B] bg-orange-50 dark:bg-orange-900/20' 
                              : 'border-zinc-200 dark:border-gray-700 hover:bg-zinc-50 dark:hover:bg-gray-800'
                            }
                            ${opt.required ? 'ring-1 ring-green-200' : ''}
                          `}
                        >
                          <Checkbox
                            checked={form.selected_price_info.includes(opt.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                updateForm('selected_price_info', [...form.selected_price_info, opt.id]);
                              } else {
                                updateForm('selected_price_info', form.selected_price_info.filter(t => t !== opt.id));
                              }
                            }}
                          />
                          <span className="text-sm">{opt.label}</span>
                          {opt.required && <span className="text-xs text-green-600">zalecane</span>}
                        </label>
                      ))}
                    </div>
                    {errors.selected_price_info && (
                      <p className="text-red-500 text-sm mt-2">{errors.selected_price_info}</p>
                    )}
                  </div>

                  {/* Pickup Date */}
                  <div>
                    <Label>Możliwy odbiór od</Label>
                    <Input
                      type="date"
                      value={form.pickup_date}
                      onChange={(e) => updateForm('pickup_date', e.target.value)}
                    />
                    <p className="text-xs text-zinc-500 mt-1">Od kiedy szczenięta będą gotowe</p>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* Step 5: Description */}
            {step === 5 && (
              <WizardStep
                key="step5"
                icon={FileText}
                title="O miocie"
                subtitle="Opis własnymi słowami pomaga klientowi poczuć charakter miotu i szczeniąt."
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="mb-0">Opis miotu</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateDescription}
                      disabled={generating}
                      data-testid="litter-ai-description-btn"
                      className="h-8 text-[#F47A3B] border-[#F47A3B]/30 hover:bg-[#F47A3B]/5"
                    >
                      {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
                      {form.description ? 'Napisz od nowa' : 'Napisz za mnie'}
                    </Button>
                  </div>
                  <Textarea
                    data-testid="litter-description"
                    value={form.description}
                    onChange={(e) => updateForm('description', e.target.value)}
                    placeholder="Opisz miot - charakter szczeniąt, warunki, dlaczego warto kupić... lub kliknij Napisz za mnie."
                    rows={8}
                  />
                  <p className="text-xs text-zinc-500">
                    ✏️ Możesz napisać sam lub kliknąć „Napisz za mnie" — opis zawsze pozostaje edytowalny.
                  </p>
                </div>
              </WizardStep>
            )}

            {/* Step 6: Images - CRITICAL FOR OFFER */}
            {step === 6 && (
              <WizardStep
                key="step6"
                icon={Camera}
                title="Zdjęcia miotu"
                subtitle="Zdjęcia decydują o pierwszym wrażeniu — dobre foto to więcej zapytań."
              >
                <div className="space-y-6">
                  {/* Zaufanie: zdjęcia zoptymalizowane pod SEO/AI */}
                  <ImageSeoBadge />
                  {/* Info banner — auto-cover from gallery */}
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300 text-sm">
                    💡 <strong>Wskazówka:</strong> Pierwsze zdjęcie z galerii zostanie automatycznie ustawione jako <strong>główne</strong>. Możesz to zmienić klikając „Ustaw jako główne" na dowolnej miniaturce.
                  </div>

                  {/* Gallery count indicator */}
                  <div className={`p-3 rounded-lg ${hasSufficientGallery(form) ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                    <div className="flex items-center gap-2">
                      {hasSufficientGallery(form) ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <AlertCircle className="w-5 h-5" />
                      )}
                      <span className="font-medium">
                        Galeria: {(form.gallery || []).length}/2 min. zdjęć
                      </span>
                    </div>
                    {!hasSufficientGallery(form) && (
                      <p className="text-xs mt-1">Mioty ze zdjęciami sprzedają się 5x szybciej</p>
                    )}
                  </div>

                  <ImageUploader
                    coverImage={form.cover_image}
                    gallery={form.gallery}
                    onCoverChange={(url) => updateForm('cover_image', url)}
                    onGalleryChange={(urls) => updateForm('gallery', urls)}
                    maxGallery={20}
                    entityType="litter"
                    entityId={id}
                  />
                  
                  {errors.cover_image && (
                    <p className="text-red-500 text-sm">{errors.cover_image}</p>
                  )}
                  {errors.gallery && (
                    <p className="text-red-500 text-sm">{errors.gallery}</p>
                  )}
                  
                  <div className="text-xs text-zinc-500 space-y-1">
                    <p>• Zdjęcie główne: min. 1600x900px</p>
                    <p>• Galeria: min. 2 zdjęcia, rozdzielczość 1200px+</p>
                  </div>

                </div>
              </WizardStep>
            )}

            {/* STEP KOŃCOWY: Podsumowanie — ujednolicony z Kreatorem Hodowli (1:1) */}
            {step === 7 && (
              <div key="step-summary">
                <div className="mb-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <FontStylePicker value={form.font_style} onChange={(v) => updateForm('font_style', v)} />
                    <LivePagePreview
                      fontStyle={form.font_style}
                      eyebrow="Szczenięta"
                      title={form.breed || 'Miot'}
                      subtitle={form.name || ''}
                      image={form.cover_image || (form.gallery && form.gallery[0]) || ''}
                      accentKey="gold"
                      theme="dark"
                    />
                  </div>
                </div>
                <WizardSummaryStep
                  headerTitle="Twój miot jest gotowy do publikacji"
                  headerSubtitle="Po publikacji miot pojawi się w katalogu APISAI i na stronie Twojej hodowli."
                  pageCardTitle="Strona miotu"
                  checklist={litterSummaryRows}
                  goToStep={goToStep}
                  catalogChecked={form.show_in_catalog}
                  onCatalogChange={(v) => updateForm('show_in_catalog', v)}
                  catalogLabel="Chcę, aby ten miot był widoczny w katalogu APISAI."
                  catalogTestId="litter-catalog-toggle"
                  termsAccepted={termsAccepted}
                  setTermsAccepted={setTermsAccepted}
                  termsTestId="litter-terms-checkbox"
                  dataAccurateTestId="litter-data-accurate"
                  onPublish={handleSubmit}
                  onSaveLater={handleSaveAndExit}
                  submitting={loading}
                  isEdit={isEdit}
                  publishLabel="Opublikuj miot"
                  testId="litter-wizard-summary"
                />
              </div>
            )}
          </AnimatePresence>
            </div>

            {/* STEP KOŃCOWY usunął dawny dolny pasek — nawigacja przez WizardTopbar + kroki / WizardMobileBar */}
          </section>

          {/* PRAWA KOLUMNA: PODGLĄD NA ŻYWO — widoczny też na kroku Podsumowanie (układ „Birch Haus": treść od lewej + podgląd po prawej) */}
          <section className="hidden lg:block w-full lg:w-[420px] xl:w-[460px] flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <LitterLivePreview form={form} />
            </div>
          </section>
        </div>
      </main>

      {/* MOBILE: wspólny WizardMobileBar (pływający podgląd + dolny pasek akcji + arkusz) */}
      <WizardMobileBar
        prefix="litter-wizard"
        step={step}
        totalSteps={totalSteps}
        isLastStep={step === totalSteps}
        submitting={loading}
        nextDisabled={loading || (step === totalSteps && !termsAccepted && !isEdit)}
        publishLabel={isEdit ? 'Zapisz zmiany' : 'Opublikuj miot'}
        onBack={handleBack}
        onSaveExit={handleSaveAndExit}
        onNext={step === totalSteps ? handleSubmit : handleNext}
        previewNode={<LitterLivePreview form={form} />}
      />
      </div>
      
      {/* Quick Add Parent Modal */}
      <Dialog open={quickAddModal.open} onOpenChange={(open) => !open && closeQuickAddModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {quickAddModal.type === 'mother' ? (
                <>
                  <span className="text-pink-500">♀</span>
                  Dodaj nową sukę
                </>
              ) : (
                <>
                  <span className="text-blue-500">♂</span>
                  Dodaj nowego psa
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Szybko dodaj {quickAddModal.type === 'mother' ? 'sukę' : 'psa'} do hodowli.
              Pełny profil możesz uzupełnić później.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <Label>Imię *</Label>
              <Input
                data-testid="quick-add-name"
                value={quickAddForm.name}
                onChange={(e) => setQuickAddForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder={quickAddModal.type === 'mother' ? 'np. Luna' : 'np. Max'}
                className="h-12"
                autoFocus
              />
            </div>
            
            {/* Breed */}
            <div>
              <Label>Rasa *</Label>
              <LitterBreedCombobox
                breeds={currentBreeds}
                value={quickAddForm.breed}
                onChange={(v) => setQuickAddForm(prev => ({ ...prev, breed: v }))}
                hasError={false}
              />
            </div>
            
            {/* Info */}
            <div className="p-3 bg-zinc-50 dark:bg-gray-800 rounded-lg text-sm text-zinc-600 dark:text-zinc-400">
              <p>• {quickAddModal.type === 'mother' ? 'Suka' : 'Pies'} zostanie dodany do hodowli <strong>{selectedKennel?.name}</strong></p>
              <p>• Możesz później uzupełnić zdjęcia, tytuły i badania</p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={closeQuickAddModal}
              disabled={quickAddLoading}
            >
              Anuluj
            </Button>
            <Button
              type="button"
              onClick={handleQuickAddSubmit}
              disabled={quickAddLoading || !quickAddForm.name || !quickAddForm.breed}
              className="bg-[#F47A3B] hover:bg-[#E07A0F]"
            >
              {quickAddLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Dodaję...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Dodaj i wybierz
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </StudioShell>
  );
}
